import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, MenuItem, Grid2 as Grid, Chip,
  Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Alert, Tooltip, IconButton, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions,
} from '@mui/material';
import {
  PointOfSale as PosIcon, Refresh as RefreshIcon, Block as BlockIcon,
  CheckCircle as ActiveIcon, LogoutOutlined as RevokeIcon, Payments as CashIcon,
} from '@mui/icons-material';
import { posAdminService, PosTerminal } from '../../services/pos-admin.service';

const ESTADO_COR: Record<string, 'success' | 'default' | 'error'> = {
  ACTIVE: 'success', INACTIVE: 'default', BLOCKED: 'error',
};

const quando = (d: string | null) => {
  if (!d) return '—';
  const dt = new Date(d);
  const dias = Math.floor((Date.now() - dt.getTime()) / 864e5);
  if (dias === 0) return dt.toLocaleTimeString('pt-PT', { timeZone: 'Atlantic/Cape_Verde', hour: '2-digit', minute: '2-digit' });
  if (dias === 1) return 'ontem';
  if (dias < 30) return `há ${dias} dias`;
  return dt.toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' });
};

/**
 * Terminais POS.
 *
 * Os terminais registam-se sozinhos no primeiro login da aplicação — não há
 * criação manual. Esta página existe para o que faltava: ver quais existem,
 * quando foram usados, e cortar o acesso de um que se perdeu.
 */
export default function PosTerminais() {
  const [linhas, setLinhas] = useState<PosTerminal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [estado, setEstado] = useState('');
  const [confirmar, setConfirmar] = useState<{ t: PosTerminal; accao: 'bloquear' | 'revogar' } | null>(null);
  const [aGuardar, setAGuardar] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setLinhas(await posAdminService.terminais({ search: search || undefined, estado: estado || undefined }));
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Não foi possível carregar os terminais.');
    } finally {
      setLoading(false);
    }
  }, [search, estado]);

  useEffect(() => { load(); }, [load]);

  const executar = async () => {
    if (!confirmar) return;
    setAGuardar(true);
    setError(null);
    try {
      const msg = confirmar.accao === 'revogar'
        ? await posAdminService.revogarSessoes(confirmar.t.id)
        : await posAdminService.mudarEstado(confirmar.t.id, 'BLOCKED');
      setSuccess(msg);
      setConfirmar(null);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Não foi possível executar a acção.');
    } finally {
      setAGuardar(false);
    }
  };

  const activar = async (t: PosTerminal) => {
    try {
      setSuccess(await posAdminService.mudarEstado(t.id, 'ACTIVE'));
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Não foi possível activar.');
    }
  };

  const semAgente = linhas.filter((t) => t.estado === 'ACTIVE' && !t.aceitaNumerario).length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
            <PosIcon color="primary" /> Terminais POS
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Registam-se sozinhos no primeiro login da aplicação. Aqui vê-se quais existem e corta-se o acesso.
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Actualizar</Button>
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {semAgente > 0 && (
        <Alert severity="info" icon={<CashIcon />}>
          <strong>{semAgente}</strong> terminal(is) activo(s) sem agente associado. Esses aceitam pagamentos
          mas <strong>não</strong> carregamentos em numerário — a entidade tem de estar registada como agente
          activo, senão a operação é recusada.
        </Alert>
      )}

      <Card>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField fullWidth size="small" label="Procurar"
                placeholder="Nome, dispositivo ou entidade"
                value={search} onChange={(e) => setSearch(e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField fullWidth size="small" select label="Estado"
                value={estado} onChange={(e) => setEstado(e.target.value)}>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="ACTIVE">Activo</MenuItem>
                <MenuItem value="INACTIVE">Inactivo</MenuItem>
                <MenuItem value="BLOCKED">Bloqueado</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : linhas.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              Nenhum terminal. Aparecem aqui depois do primeiro login na aplicação POS.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Terminal</TableCell>
                    <TableCell>Entidade</TableCell>
                    <TableCell align="center">Sessões</TableCell>
                    <TableCell>Última actividade</TableCell>
                    <TableCell align="center">Numerário</TableCell>
                    <TableCell align="center">Estado</TableCell>
                    <TableCell align="right">Acções</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {linhas.map((t) => (
                    <TableRow key={t.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{t.deviceName ?? 'Sem nome'}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {t.deviceId}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{t.entidadeNome ?? '—'}</TableCell>
                      <TableCell align="center">
                        {t.sessoesActivas > 0 ? (
                          <Chip size="small" label={t.sessoesActivas} color="success" />
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{quando(t.ultimaActividade)}</TableCell>
                      <TableCell align="center">
                        {t.aceitaNumerario ? (
                          <Tooltip title={`Agente ${t.agenteCodigo}`}>
                            <Chip size="small" label="Sim" color="success" variant="outlined" />
                          </Tooltip>
                        ) : (
                          <Tooltip title="A entidade não está registada como agente activo">
                            <Chip size="small" label="Não" variant="outlined" />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={t.estado} color={ESTADO_COR[t.estado] ?? 'default'} />
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="Terminar sessões abertas">
                          <span>
                            <IconButton size="small" disabled={t.sessoesActivas === 0}
                              onClick={() => setConfirmar({ t, accao: 'revogar' })}>
                              <RevokeIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {t.estado === 'ACTIVE' ? (
                          <Tooltip title="Bloquear terminal">
                            <IconButton size="small" color="error"
                              onClick={() => setConfirmar({ t, accao: 'bloquear' })}>
                              <BlockIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Activar terminal">
                            <IconButton size="small" color="success" onClick={() => activar(t)}>
                              <ActiveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmar != null} onClose={() => setConfirmar(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {confirmar?.accao === 'revogar' ? 'Terminar sessões?' : 'Bloquear terminal?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmar?.accao === 'revogar'
              ? `O terminal "${confirmar?.t.deviceName ?? confirmar?.t.deviceId}" terá de fazer login outra vez. Use isto se o dispositivo foi perdido ou mudou de mãos.`
              : `O terminal "${confirmar?.t.deviceName ?? confirmar?.t.deviceId}" deixa de poder operar e as sessões abertas são terminadas de imediato.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmar(null)} disabled={aGuardar}>Cancelar</Button>
          <Button variant="contained" color={confirmar?.accao === 'revogar' ? 'primary' : 'error'}
            onClick={executar} disabled={aGuardar}>
            {aGuardar ? 'A executar…' : confirmar?.accao === 'revogar' ? 'Terminar' : 'Bloquear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
